import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { CANVAS_ITEM_MARGIN } from 'components/ReportCanvas/canvasConstants';

import { ElementLayout, Span, resizeSpan } from '../compositeUtils';
import { NO_DRAG_CLASS } from '../constants';

// The handles' and their marks' classes, so a hovered cell can hint at them
// (see CompositeCard)
export const RESIZE_HANDLES_CLASS = 'studio-composite-resize-handles';
export const RESIZE_HANDLE_CLASS = 'studio-composite-resize-handle';

// Resizing snaps to the canvas's backdrop cells (see CELL_SIZE in
// screens/Studio/constants): a column wide, two grid rows tall
const GRID_STEP = { cols: 1, rows: 2 };

// As a block's on the canvas (see StudioCanvas): invisible strips along each
// edge, straddling the outline, and squares on the corners
const EDGE = 10;
const CORNER = 16;
const CORNER_MARK_INSET = 2.5;

export type ResizeEdge = 'n' | 'e' | 's' | 'w' | 'ne' | 'nw' | 'se' | 'sw';
export const RESIZE_EDGES: ResizeEdge[] = [
  'n',
  'e',
  's',
  'w',
  'ne',
  'nw',
  'se',
  'sw',
];

const isCorner = (edge: ResizeEdge) => edge.length === 2;

const EDGE_POSITIONS: Record<ResizeEdge, string> = {
  n: `top: -${EDGE / 2}px; left: 0; right: 0; height: ${EDGE}px;`,
  s: `bottom: -${EDGE / 2}px; left: 0; right: 0; height: ${EDGE}px;`,
  e: `right: -${EDGE / 2}px; top: 0; bottom: 0; width: ${EDGE}px;`,
  w: `left: -${EDGE / 2}px; top: 0; bottom: 0; width: ${EDGE}px;`,
  ne: `top: -${CORNER / 2}px; right: -${CORNER / 2}px;`,
  nw: `top: -${CORNER / 2}px; left: -${CORNER / 2}px;`,
  se: `bottom: -${CORNER / 2}px; right: -${CORNER / 2}px;`,
  sw: `bottom: -${CORNER / 2}px; left: -${CORNER / 2}px;`,
};

// Pills along the middle of each edge, circles set in from the corners
const MARKS: Record<ResizeEdge, string> = {
  n: 'width: 16px; height: 6px; border-radius: 3px;',
  s: 'width: 16px; height: 6px; border-radius: 3px;',
  e: 'width: 6px; height: 16px; border-radius: 3px;',
  w: 'width: 6px; height: 16px; border-radius: 3px;',
  ne: `width: 9px; height: 9px; border-radius: 50%; transform: translate(calc(-50% - ${CORNER_MARK_INSET}px), calc(-50% + ${CORNER_MARK_INSET}px));`,
  nw: `width: 9px; height: 9px; border-radius: 50%; transform: translate(calc(-50% + ${CORNER_MARK_INSET}px), calc(-50% + ${CORNER_MARK_INSET}px));`,
  se: `width: 9px; height: 9px; border-radius: 50%; transform: translate(calc(-50% - ${CORNER_MARK_INSET}px), calc(-50% - ${CORNER_MARK_INSET}px));`,
  sw: `width: 9px; height: 9px; border-radius: 50%; transform: translate(calc(-50% + ${CORNER_MARK_INSET}px), calc(-50% - ${CORNER_MARK_INSET}px));`,
};

const CURSORS: Record<ResizeEdge, string> = {
  n: 'ns-resize',
  s: 'ns-resize',
  e: 'ew-resize',
  w: 'ew-resize',
  ne: 'nesw-resize',
  sw: 'nesw-resize',
  nw: 'nwse-resize',
  se: 'nwse-resize',
};

// Over the cell's outline (inset as it is), taking no pointer itself
const Overlay = styled.div<{ $insetX: number; $isSelected: boolean }>`
  position: absolute;
  inset: 0 ${(props) => props.$insetX}px;
  z-index: 3;
  pointer-events: none;

  ${(props) =>
    props.$isSelected
      ? `
    .${RESIZE_HANDLE_CLASS}::after {
      opacity: 1;
      border-color: var(--color-studio-selection);
    }
  `
      : ''}
`;

const Handle = styled.div<{ $edge: ResizeEdge; $cursor: string }>`
  position: absolute;
  ${(props) => EDGE_POSITIONS[props.$edge]}
  ${(props) =>
    isCorner(props.$edge)
      ? `width: ${CORNER}px; height: ${CORNER}px; z-index: 1;`
      : ''}
  cursor: ${(props) => props.$cursor};
  pointer-events: auto;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    box-sizing: border-box;
    border: 1.5px solid var(--color-studio-selection-subtle);
    background: var(--color-bg-raised);
    transform: translate(-50%, -50%);
    opacity: 0;
    transition: opacity 120ms ease-out;
    pointer-events: none;
    ${(props) => MARKS[props.$edge]}
  }
`;

// The cell's size as it's dragged
const SizesBadge = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 1px 6px;
  border-radius: 4px;
  background: var(--color-studio-selection);
  color: var(--color-text-inverse);
  font-size: 10px;
  font-weight: 600;
  line-height: 14px;
  white-space: nowrap;
  pointer-events: none;
`;

// A cell's resize handles, as a block's on the canvas: on every edge and
// corner (or just those given). Marked faintly while it's hovered (see
// CompositeCard), and fully while it's selected.
const CellResizeHandles = ({
  edges = RESIZE_EDGES,
  insetX,
  isSelected,
  sizes,
  onStart,
  onReset,
  testId,
}: {
  edges?: ResizeEdge[];
  insetX: number;
  isSelected: boolean;
  // While dragged: its size, as shown
  sizes?: string;
  onStart: (e: React.MouseEvent<HTMLDivElement>, edge: ResizeEdge) => void;
  onReset: (edge: ResizeEdge) => void;
  testId: string;
}) => (
  <Overlay
    className={RESIZE_HANDLES_CLASS}
    $insetX={insetX}
    $isSelected={isSelected || !!sizes}
  >
    {edges.map((edge) => (
      <Handle
        key={edge}
        className={`${NO_DRAG_CLASS} ${RESIZE_HANDLE_CLASS}`}
        $edge={edge}
        $cursor={CURSORS[edge]}
        title="Drag to resize. Double-click to size automatically."
        onMouseDown={(e) => onStart(e, edge)}
        onDoubleClick={() => onReset(edge)}
        data-testid={`${testId}-${edge}`}
      />
    ))}
    {sizes && <SizesBadge data-testid={`${testId}-sizes`}>{sizes}</SizesBadge>}
  </Overlay>
);

// A resize under way: the layouts it would save (every element in the
// dragged one's row), and the dragged one's size, as shown
export type ResizePreview = {
  elementId: string;
  layouts: Record<string, ElementLayout>;
  sizes: string;
};

const snap = ({ at, step }: { at: number; step: number }) =>
  step > 0 ? Math.round(at / step) * step : at;

// Dragging one of a cell's handles resizes just it, snapped to the canvas
// grid: from its right or left edge into the free space beside it (see
// resizeSpan), from its bottom or top edge taller or shorter, its row
// growing to fit. Nothing else in the row moves or resizes: their sizes are
// measured as the drag starts and kept, so an auto-sized row becomes
// explicit. Saved once, on release, if it moved.
export const useCompositeResize = ({
  layout,
  onResized,
}: {
  // The block's size in grid units, which its grid steps divide
  layout: Studio.CardLayout;
  onResized: (resized: ResizePreview) => void;
}) => {
  const [preview, setPreview] = useState<ResizePreview | undefined>();
  const endDragRef = useRef<(() => void) | undefined>();
  useEffect(() => () => endDragRef.current && endDragRef.current(), []);

  const startResize = useCallback(
    ({
      e,
      edge,
      block,
      rowsEl,
      rowEl,
      cells,
      elements,
      index,
      gap,
      rowPaddingX = 0,
      reserved,
      rowsScale,
    }: {
      e: React.MouseEvent<HTMLDivElement>;
      edge: ResizeEdge;
      block: HTMLElement;
      // The block's rows, whose height a share of the block's is of
      rowsEl: HTMLElement;
      rowEl: HTMLElement;
      // The row's cells and their elements, in order
      cells: HTMLElement[];
      elements: Studio.CompositeElement[];
      // The one dragged
      index: number;
      // Between the row's cells (px), shared out among them
      gap: number;
      // Inside the row's sides (px): its cells' shares are of the width
      // within it
      rowPaddingX?: number;
      // Kept clear at the end of the row (its "+" slot), if anything
      reserved?: HTMLElement;
      // How much the rows' set heights are scaled down by (see getRowsScale)
      rowsScale: number;
    }) => {
      // Not a press on the cell or block: nothing is selected on release
      e.preventDefault();
      e.stopPropagation();
      if (e.button !== 0 || !cells[index] || !elements[index]) {
        return;
      }
      const blockRect = block.getBoundingClientRect();
      const rects = cells.map((cell) => cell.getBoundingClientRect());
      const across = edge.includes('e')
        ? 'end'
        : edge.includes('w')
          ? 'start'
          : undefined;
      const down = edge.includes('s')
        ? 'end'
        : edge.includes('n')
          ? 'start'
          : undefined;

      // Across: shares of the row's width, the cells' gaps shared out
      const rowWidth = rowEl.getBoundingClientRect().width - 2 * rowPaddingX;
      const gapShare = (gap * (cells.length - 1)) / cells.length;
      const spansX: Span[] = elements.map((element, i) => ({
        offset: element.offsetPercent ?? 0,
        size: rowWidth > 0 ? ((rects[i].width + gapShare) / rowWidth) * 100 : 0,
      }));
      // The cells' share of the row, less what's kept clear and its gap
      const rowTotal =
        rowWidth > 0 && reserved
          ? 100 -
            ((reserved.getBoundingClientRect().width + gap) / rowWidth) * 100
          : 100;
      const colStep =
        ((blockRect.width + CANVAS_ITEM_MARGIN) / layout.w) * GRID_STEP.cols;
      const edgeX =
        (across === 'end' ? rects[index].right : rects[index].left) -
        blockRect.left;

      // Down: shares of the block's (rows') height
      const percentPx =
        (rowsEl.getBoundingClientRect().height * rowsScale) / 100;
      const spansY: Span[] = elements.map((element, i) => ({
        offset: element.offsetTopPercent ?? 0,
        size: percentPx > 0 ? rects[i].height / percentPx : 0,
      }));
      const rowStep =
        ((blockRect.height + CANVAS_ITEM_MARGIN) / layout.h) * GRID_STEP.rows;
      const edgeY =
        (down === 'end' ? rects[index].bottom : rects[index].top) -
        blockRect.top;

      const start = { x: e.clientX, y: e.clientY };
      const round = (n: number) => Math.round(n * 10) / 10;
      let latest: ResizePreview | undefined;
      const onMove = (move: MouseEvent) => {
        const layouts: Record<string, ElementLayout> = {};
        const sizes: string[] = [];
        if (across && rowWidth > 0) {
          const moved = snap({
            at: edgeX + move.clientX - start.x,
            step: colStep,
          });
          const spans = resizeSpan({
            spans: spansX,
            index,
            edge: across,
            delta: ((moved - edgeX) / rowWidth) * 100,
            min: (colStep / rowWidth) * 100,
            total: rowTotal,
          });
          elements.forEach((element, i) => {
            layouts[element.id] = {
              widthPercent: round(spans[i].size),
              offsetPercent: round(spans[i].offset),
            };
          });
          sizes.push(`${Math.round(spans[index].size)}% wide`);
        }
        if (down && percentPx > 0) {
          const moved = snap({
            at: edgeY + move.clientY - start.y,
            step: rowStep,
          });
          const [resized] = resizeSpan({
            spans: [spansY[index]],
            index: 0,
            edge: down,
            delta: (moved - edgeY) / percentPx,
            min: rowStep / percentPx,
            total: Infinity,
          });
          elements.forEach((element, i) => {
            const span = i === index ? resized : spansY[i];
            layouts[element.id] = {
              ...layouts[element.id],
              heightPercent: round(span.size),
              offsetTopPercent: round(span.offset),
            };
          });
          sizes.push(`${Math.round(resized.size)}% tall`);
        }
        latest = {
          elementId: elements[index].id,
          layouts,
          sizes: sizes.join(' · '),
        };
        setPreview(latest);
      };
      const endDrag = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        endDragRef.current = undefined;
      };
      const onUp = () => {
        endDrag();
        setPreview(undefined);
        if (latest) {
          onResized(latest);
        }
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
      endDragRef.current = endDrag;
    },
    [layout.h, layout.w, onResized],
  );

  return { preview, startResize };
};

export default CellResizeHandles;
